/**
 * Whether a failed create looks like "the request never reached the backend".
 *
 * Deliberately broader than the shared desktop-connectivity classifier in
 * `./desktopOfflineNetwork`: it also catches the mutation's own 60s
 * `AbortSignal.timeout`, and it answers on the error alone with no reading of
 * the published connectivity state.
 *
 * Single-sited because two call sites have to agree on one error: the
 * offline-capture fallback in `useNewSession` and the toast suppression in
 * `useMutateCreateNewSession`. If they could answer differently, a create the
 * fallback rescues would still raise a red network toast.
 */
export const isNetworkCreateError = (error: unknown): boolean => {
  if (error instanceof DOMException && error.name === 'TimeoutError') {
    return true;
  }

  if (error instanceof Error) {
    return /network|offline|timeout|failed to fetch|ERR_NETWORK/i.test(error.message);
  }

  if (typeof error === 'object' && error !== null) {
    const maybeNetworkError = error as { code?: unknown; message?: unknown };
    return (
      maybeNetworkError.code === 'ERR_NETWORK' ||
      (typeof maybeNetworkError.message === 'string' &&
        /network|offline|timeout|failed to fetch/i.test(maybeNetworkError.message))
    );
  }

  return false;
};

/** Whether the backend actually answered, i.e. this is a real server error. */
export const hasServerResponse = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  (error as { response?: unknown }).response != null;
